<template>
  <div>
    <div v-if="!txHash">
      <div class="heading-4 font-weight-medium">Terms & Conditions</div>
      <div class="mt-8 text-5 line-height-25 darkgray--text text-justify">
        Using Unagii Vaults means you will be supplying assets to third-party
        pools where rates are determined by third-party protocols. You
        understand that Unagii is a technical service provider that allows you
        to access third-party protocols integrated by Unagii. You are aware of
        the risks involved and wish to continue to approve access and sign this
        transaction.
      </div>
      <div class="mt-8 text-6 darkgray--text smoke rounded-10 pa-5">
        By accepting, you agree to Unagii’s
        <a
          href="https://www.unagii.com/legal/terms"
          target="_blank"
          rel="noopener noreferrer"
          class="text-decoration-none"
          >Terms & Conditions</a
        >
        and
        <a
          href="https://www.unagii.com/legal/privacy"
          target="_blank"
          rel="noopener noreferrer"
          class="text-decoration-none"
          >Privacy Policy</a
        >.
      </div>
      <div class="mt-10 d-flex align-center justify-end">
        <div
          class="text-4 font-weight-medium gray--text clickable"
          @click="$emit('close')"
        >
          Cancel
        </div>
        <c-btn class="ml-5" @click="approve">Approve</c-btn>
      </div>
      <sign-tx
        v-if="shouldShowTx"
        :txObject="txObject"
        txType="approve"
        :vault="vault"
        :vaultToken="vaultToken"
        :metadata="{
          isZapIn: zap === 'in' || undefined,
          isZapOut: zap === 'out' || undefined,
        }"
        @onError="onError"
        @onSuccess="onSuccess"
        title="Processing approval transaction"
        description="Approval of transaction is pending. Please do not refresh until the transaction is confirmed."
      />
    </div>
    <div class="d-flex flex-column align-center" v-else>
      <div class="heading-4 font-weight-medium text-center">
        Processing approval transaction
      </div>
      <div class="mt-5 darkgray--text text-4 line-height-25 text-center">
        Approval of transaction is pending. Please do not refresh until the
        transaction is confirmed.
      </div>
      <v-progress-circular
        indeterminate
        :size="80"
        :width="6"
        color="primary"
        class="mt-10"
      ></v-progress-circular>
      <div class="mt-10 text-4">
        <a
          :href="project.config.explorerTxsLink + txHash"
          target="_blank"
          rel="noopener noreferrer"
          class="text-decoration-none outline-none"
          >Click to view status of transaction</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { sleep, fetchEthereumTxStatus } from "@/common/lib/utils";
import SignTx from "./SignTx";

export default {
  components: {
    SignTx,
  },
  props: ["vault", "token", "vaultToken", "value", "zap", "txObject"],
  data: () => ({
    shouldShowTx: false,
    rawTx: null,
    txHash: null,
  }),
  computed: mapGetters({
    isTxConfirmed: "notification/isTxConfirmed",
    project: "projectConfig/latestProject",
  }),
  methods: {
    approve() {
      this.shouldShowTx = true;
    },
    onError(error) {
      this.$emit("onError", error);
    },
    async onSuccess(txHash) {
      try {
        const { confirmed, failed } = await Promise.race([
          this.checkTxStatus(txHash),
          this.checkTxStatusEtherscan(txHash),
        ]);
        if (failed)
          return this.onError(
            new Error("Something went wrong. Please refresh the page and try again.")
          );
        if (confirmed) {
          this.$emit("onSuccess", txHash);
        }
      } catch (error) {
        this.onError(error);
      }
    },
    async checkTxStatusEtherscan(txHash, retries = 0) {
      const result = await fetchEthereumTxStatus(txHash);
      if (!result.pending) {
        return result;
      }
      if (retries >= 20) {
        return {
          failed: true,
        };
      }
      await sleep(6000);
      return this.checkTxStatusEtherscan(txHash, retries);
    },
    async checkTxStatus(txHash, retries = 0) {
      if (this.isTxConfirmed(txHash)) {
        return {
          confirmed: true,
        };
      }
      if (retries >= 120) {
        return {
          failed: true,
        };
      }
      await sleep(1000);
      return this.checkTxStatus(txHash, retries + 1);
    },
  },
  watch: {
    value: {
      immediate: true,
      async handler(val) {
        this.shouldShowTx = false;
      },
    },
  },
};
</script>
